import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { BackgroundPosition } from '../WatchProjectHero'

interface ResponsiveBackgroundContainerTestProps {
  cloudinaryImgPath: string
  cloudinaryTabletImgPath?: string
  cloudinaryMobileImgPath?: string
  theaterBackgroundPosition?: BackgroundPosition
  contentRef: React.RefObject<HTMLDivElement>
}

export const ResponsiveBackgroundContainerTest = React.forwardRef<
  HTMLDivElement,
  ResponsiveBackgroundContainerTestProps
>((props, ref) => {
  const { cloudinaryImgPath, cloudinaryMobileImgPath, cloudinaryTabletImgPath, theaterBackgroundPosition, contentRef } =
    props
  const [contentHeight, setContentHeight] = useState<number | null>(null)

  useEffect(() => {
    const calculateHeight = () => {
      if (contentRef.current) {
        const height = contentRef.current.getBoundingClientRect().height
        setContentHeight(height)
      }
    }

    calculateHeight()

    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [contentRef])

  return (
    <div
      ref={ref}
      className="pointer-events-none absolute left-0 top-0 z-0 min-h-[56vh] w-full transition-opacity md:min-h-[60vh] lg:min-h-[70vh] xl:min-h-[80vh]"
      style={{ minHeight: contentHeight ? contentHeight : '80vh' }}
    >
      <div className="block lg:hidden">
        <div className="block md:hidden">
          {/* Mobile image */}
          <ResponsiveBackgroundImage
            preBackgroundStyles=""
            src={cloudinaryMobileImgPath ?? cloudinaryImgPath}
            className="aspect-h-3 aspect-w-2 block h-full w-full bg-bottom"
            transforms="g_face,c_fill"
          />
        </div>
        <div className="hidden md:block">
          {/* Tablet image */}
          <ResponsiveBackgroundImage
            preBackgroundStyles=""
            src={cloudinaryTabletImgPath ?? cloudinaryImgPath}
            className="bg-top-right aspect-h-9 aspect-w-16 block w-full"
            transforms="g_face,c_fill"
          />
        </div>
      </div>
      <div className="hidden pb-12 lg:block">
        {/* Desktop image */}
        <ResponsiveBackgroundImage
          preBackgroundStyles=""
          src={cloudinaryImgPath}
          className={classNames(
            'h-full w-full bg-top-right aspect-w-16 aspect-h-9 absolute top-0 right-0',
            theaterBackgroundPosition,
          )}
          transforms="g_face,c_fill"
        />
      </div>
      <ResponsiveBackgroundImage
        preBackgroundStyles=""
        src={getCloudinaryImageUrl({
          path: '/v1743018721/angel-studios/watch/hero/Glows_Desktop',
          transforms: 'f_auto,q_auto',
        })}
        className={'invisible bg-left-top lg:visible lg:z-10'}
      />
      <ResponsiveBackgroundImage
        preBackgroundStyles=""
        src={getCloudinaryImageUrl({
          path: '/v1743018773/angel-studios/watch/hero/Glows_Mobile',
          transforms: 'f_auto,q_auto',
        })}
        className={'visible z-10 bg-left-top lg:invisible'}
      />
      {/* Left -> Right Gradient */}
      <div
        className={classNames('absolute inset-0 left-0 top-0 h-full min-w-1/2 xl:min-w-[60%] transition-opacity')}
        style={{
          background:
            'linear-gradient(90deg, rgba(21, 21, 21, 0.9) 0%, rgba(21, 21, 21, 0.7) 34.24%, rgba(21, 21, 21, 0) 69.25%)',
        }}
      />
      {/* Top -> Bottom Gradient */}
      <div
        className={classNames('absolute inset-0 left-0 top-0 min-h-1/6 w-full transition-opacity')}
        style={{
          background: 'linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 30%)',
          height: contentHeight ? contentHeight + 150 : '80vh',
        }}
      />
      {/* Bottom -> Top Gradient Mobile */}
      <div
        className={classNames('absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity lg:hidden')}
        style={{
          background: 'linear-gradient(0deg, #141414 28.15%, rgba(21, 21, 21, 0) 70%)',
          height: contentHeight ? contentHeight + 150 : '80vh',
        }}
      />
      {/* Bottom -> Top Gradient Desktop */}
      <div
        className={classNames('absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity hidden lg:block')}
        style={{
          background: 'linear-gradient(0deg, #141414 18.15%, rgba(21, 21, 21, 0) 70%)',
          height: contentHeight ? contentHeight + 150 : '80vh',
        }}
      />
    </div>
  )
})

ResponsiveBackgroundContainerTest.displayName = 'ResponsiveBackgroundContainerTest'
